/**
 * PlatformAvatars
 *
 * Stacked circle avatars showing every platform a post was cross-published to.
 * Each circle is filled with the platform's brand colour and the white brand
 * icon centred — matches the pattern used in Dashboard's SomethingWorkedSection
 * and elsewhere.
 *
 * Each circle is wrapped in our brand Tooltip so hovering shows the platform
 * display name. When `channelNames` is provided (same order as `platforms`),
 * the tooltip surfaces the channel name + platform like "@brand_handle - Instagram".
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPlatformInfo } from '@/constants/platforms';
import { Tooltip } from '@/components/ui/Tooltip';

interface PlatformAvatarsProps {
  /** Uppercase platform enum strings (e.g. 'LINKEDIN', 'INSTAGRAM'). */
  platforms: string[];
  /** Optional per-circle channel display names. Same length & order as `platforms`. */
  channelNames?: (string | null)[];
  /**
   * Optional per-circle channel profile photo URLs. Same length & order as `platforms`.
   * When a URL is provided and non-null the circle shows the photo with a small
   * platform-icon badge in the bottom-right corner. Falls back to the existing
   * colored circle + centred icon when absent or null.
   */
  profilePhotoUrls?: (string | null)[];
  /** Outer circle size in pixels. Icon scales to ~50% of this. */
  size?: number;
}

export const PlatformAvatars = ({
  platforms,
  channelNames,
  profilePhotoUrls,
  size = 18,
}: PlatformAvatarsProps) => {
  if (!platforms || platforms.length === 0) return null;
  const iconSize = Math.max(8, Math.round(size * 0.5));
  // Badge size for the platform icon overlay when a profile photo is shown.
  const badgeSize = Math.max(6, Math.round(size * 0.45));
  return (
    <div className="flex items-center -space-x-1.5 isolate">
      {platforms.map((p, i) => {
        const info = getPlatformInfo(p);
        const channel = channelNames?.[i];
        const photoUrl = profilePhotoUrls?.[i] ?? null;
        const tooltipContent = channel ? `${channel} - ${info.name}` : info.name;
        return (
          <Tooltip key={`${p}-${i}`} content={tooltipContent} side="top" delayDuration={200}>
            <div
              className="rounded-full ring-2 ring-white flex items-center justify-center shrink-0 relative overflow-visible"
              style={{
                width: size,
                height: size,
                backgroundColor: info.color,
                zIndex: platforms.length - i,
              }}
              aria-label={tooltipContent}
            >
              {photoUrl ? (
                <>
                  {/* Profile photo — fills the circle; on error hides itself so the
                      platform-color background acts as a fallback. */}
                  <img
                    src={photoUrl}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  {/* Small platform-icon badge — bottom-right corner */}
                  <div
                    className="absolute flex items-center justify-center rounded-full ring-1 ring-white"
                    style={{
                      width: badgeSize + 2,
                      height: badgeSize + 2,
                      backgroundColor: info.color,
                      bottom: -1,
                      right: -1,
                    }}
                    aria-hidden
                  >
                    <FontAwesomeIcon
                      icon={info.icon}
                      className="text-white block"
                      style={{
                        fontSize: badgeSize * 0.65,
                        width: badgeSize * 0.65,
                        height: badgeSize * 0.65,
                        lineHeight: 1,
                      }}
                    />
                  </div>
                </>
              ) : (
                <FontAwesomeIcon
                  icon={info.icon}
                  className="text-white block"
                  style={{
                    fontSize: iconSize,
                    width: iconSize,
                    height: iconSize,
                    lineHeight: 1,
                  }}
                  aria-hidden
                />
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};
